import { ERROR_MESSAGES } from "../../shared/errors";
import { BUILTIN_SCHEMA } from "../config";
import { EnvxError, EnvxSchema, EnvxVarSchema } from "../types";
import path from "path";

export function toBoolean(
  value: any,
  key?: string,
  _throw = false,
  checkOnly = false
): boolean {
  const normalized = typeof value === "string" ? value.toLowerCase() : value;

  const isValid =
    normalized === true ||
    normalized === false ||
    normalized === "true" ||
    normalized === "false" ||
    normalized === 1 ||
    normalized === 0;

  if (checkOnly) return isValid;

  if (!isValid) {
    if (_throw) {
      throw new EnvxError(
        ERROR_MESSAGES.lib.validator.invalidBoolean(key || "", value)
      );
    }
    return false;
  }

  if (normalized === true || normalized === "true" || normalized === 1) {
    return true;
  }

  return false;
}

export const isEmail = (value: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

function inferTypeFromValue(value: any): EnvxVarSchema["type"] {
  const isBoolean = toBoolean(value, "", false, true);
  if (isBoolean) return "boolean";
  if (!isNaN(Number(value)) && value !== "" && value !== null) return "number";
  return "string";
}

/**
 * Completes the environment variable schema by adding entries for variables
 * present in the `.envx` file but missing in the user-defined schema.
 *
 * For each variable without an explicit schema:
 * - Infers the type from its value (string, number, boolean).
 * - Marks it as optional (`required: false`).
 *
 * Also merges built-in environment variable schemas.
 *
 * This ensures every environment variable has a schema definition,
 * even if not explicitly declared by the user.
 *
 * @param env - List of environment variables from `.envx` with their values.
 * @param schema - User-defined environment schema with explicit variable definitions.
 * @returns A fully completed schema including built-in, user-defined, and inferred entries.
 */
export function fillMissingSchemaEntries(
  env: { key: string; value: any }[],
  schema: EnvxSchema
): EnvxSchema {
  const completedSchema: EnvxSchema = { ...BUILTIN_SCHEMA, ...schema };

  env.forEach(({ key, value }) => {
    if (!completedSchema[key]) {
      completedSchema[key] = {
        type: inferTypeFromValue(value),
        required: false,
      };
    }
  });

  return completedSchema;
}

export function addBuiltinEnvVarsToSchema(schema?: EnvxSchema): EnvxSchema {
  return {
    ...BUILTIN_SCHEMA,
    ...(schema || {}),
  };
}

/**
 * Resolves a given file path to an absolute path.
 *
 * If the input path is already absolute, it is returned as-is.
 * Otherwise, it is resolved relative to the current working directory.
 *
 * @param inputPath - The file path to resolve.
 * @returns The absolute file path.
 */
export function resolveEnvFilePath(inputPath: string): string {
  return path.isAbsolute(inputPath)
    ? inputPath
    : path.resolve(process.cwd(), inputPath);
}
